import React, { useEffect, useRef, useState } from "react";

const RouteForwardSection = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.4 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <section
      ref={sectionRef}
      className=" text-white px-4 sm:px-6 md:px-12 py-20 overflow-hidden"
    >
      <style>{`
        .fade-in { animation: fadeIn 0.8s ease-out forwards; }
        .glow-line { animation: glowLine 1.5s ease-out forwards; }
        .pop-in { animation: popIn 0.6s ease-out forwards; }
        .hover-pop { transition: transform 0.3s ease; }
        .hover-pop:hover { transform: scale(1.03); }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes glowLine {
          0% { stroke: #ffffff; stroke-width: 0; opacity: 0; }
          100% { stroke: #00B2FF; stroke-width: 1; opacity: 1; }
        }

        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.8); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* Top Text Block */}
      <div className={`max-w-screen-xl mx-auto text-center mb-16 ${hasAnimated ? "fade-in" : "opacity-0"}`}>
        <h2 className="text-4xl md:text-5xl font-bold mb-6">The Route Forward</h2>
        <p className="text-lg md:text-xl leading-relaxed mb-4">
          To become the most dependable name in logistics, a brand known not just for how far it can go,
          but how well it gets there. Big in scale and blue in spirit.
        </p>
        <p className="text-lg md:text-xl leading-relaxed">
          We aim to lead the future of freight with smarter systems, stronger partnerships!{" "}
          <span className="text-[#00b9f2] font-bold">
            “Connecting Continents, Delivering Confidence”
          </span>{" "}
          isn’t just our tagline, it’s the future we’re building every single day.
        </p>
      </div>

      {/* Connector SVG + Circle */}
      <div className="relative w-full h-[120px] flex items-end justify-center my-12">
        <svg
          viewBox="0 0 1000 145"
          className="absolute top-0 left-0 w-full h-full"
          preserveAspectRatio="xMidYMid"
        >
          <path
            d="M 0 60 
               H 450 
               Q 490 60 500 100 
               Q 510 60 550 60 
               H 1000"
            className={hasAnimated ? "glow-line" : ""}
            fill="none"
          />
        </svg>

        <div className="relative z-10">
          <div className="bg-[#03baf4] text-[white] text-sm font-bold px-5 py-2 rounded-full shadow-md">
            Our Values
          </div>
        </div>
      </div>

      {/* Headline */}
      <div className={`text-center mb-14 ${hasAnimated ? "fade-in" : "opacity-0"}`}>
        <h3 className="text-4xl md:text-5xl font-bold mb-2">
          These Aren't Just Values,
        </h3>
        <p className="text-xl font-semibold text-[#00b9f2]">
          They're The Rules That Move Us
        </p>
      </div>

      {/* Value Cards */}
      <div className="max-w-screen-lg mx-auto space-y-8 px-2">
        {/* Top Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className={`bg-[#03baf4] text-white p-8 rounded-3xl hover-pop ${hasAnimated ? "pop-in" : "opacity-0"}`}>
            <h4 className="font-bold mb-2 text-xl">Clarity In Every Move</h4>
            <p className="text-sm leading-relaxed">
              We remove noise from the process. Clear systems, clear updates, clear answers.
              No delays and no confusion.
            </p>
          </div>

          <div className={`bg-[#03baf4] text-white p-8 rounded-3xl hover-pop ${hasAnimated ? "pop-in delay-100" : "opacity-0"}`}>
            <h4 className="font-bold mb-2 text-xl">Built For Pressure</h4>
            <p className="text-sm leading-relaxed">
              When the routes change or the pressure’s on, we don’t break. We adapt, respond and keep cargo moving.
            </p>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className={`bg-[#03baf4] text-white p-6 rounded-3xl hover-pop ${hasAnimated ? "pop-in delay-200" : "opacity-0"}`}>
            <h4 className="font-bold mb-2 text-xl">Connection Is The Cargo</h4>
            <p className="text-sm leading-relaxed">
              It’s not just about what we move, it’s about who we move it for. Every load builds trust.
            </p>
          </div>

          <div className={`bg-[#03baf4] text-white p-6 rounded-3xl hover-pop ${hasAnimated ? "pop-in delay-300" : "opacity-0"}`}>
            <h4 className="font-bold mb-2 text-xl">Controlled By Design</h4>
            <p className="text-sm leading-relaxed">
              We don’t leave things to chance. From port to pallet, every detail is engineered for control and compliance.
            </p>
          </div>

          <div className={`bg-[#03baf4] text-white p-6 rounded-3xl hover-pop ${hasAnimated ? "pop-in delay-400" : "opacity-0"}`}>
            <h4 className="font-bold mb-2 text-xl">Blue Is The Standard</h4>
            <p className="text-sm leading-relaxed">
              Air Blue. Ocean Blue. Sky Blue. Precision in every layer. Calm in every challenge. That’s the Big Blue way.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RouteForwardSection;
